import { LinkOutlined, MonitorOutlined } from '@ant-design/icons';
import { Access, AccessSource, SubscriptionAccessStatus, User } from '@prisma/client';
import { Flex, Popover, Spin, Tag } from 'antd';
import { startCase } from 'lodash';

import { FeatureFlags, Theme } from '@app/web-components';

import { ApproverTable } from '../../../../subscriptions/modals/approver-table';
import { useGetMembershipStatus } from '../../../access/data';

export type SubscriptionAccessStatusRendererProps = {
  access?: Access;
  status: SubscriptionAccessStatus;
  user?: User;
};

export const SubscriptionAccessStatusRendererNew = ({
  access,
  status,
  user,
}: SubscriptionAccessStatusRendererProps) => {
  const { token } = Theme.useToken();
  const { data, isPending } = useGetMembershipStatus(user?.enterpriseId, access?.externalId, access?.accessSource);

  switch (status) {
    case SubscriptionAccessStatus.NEW:
    case SubscriptionAccessStatus.QUEUED:
      return <Tag color="#8c8c8c">{getStatusText(status)}</Tag>;
    case SubscriptionAccessStatus.RETRY:
    case SubscriptionAccessStatus.WAITING_ON_DEPENDENCY:
      return (
        <Tag style={{ color: token['carbon-shade-2'] }} color={token['bright-warning']}>
          {getStatusText(status)}
        </Tag>
      );
    case SubscriptionAccessStatus.ERROR:
      return <Tag color={token['bright-error']}>{getStatusText(status)}</Tag>;
    case SubscriptionAccessStatus.CANCELLED:
      return '';
  }

  if (access && access.accessSource !== AccessSource.IEM && user) {
    if (isPending) {
      return <Spin />;
    }

    if (data) {
      const href = data.pendingTraining
        ? 'https://goto.intel.com/saba'
        : 'https://ags.intel.com/identityiq/identityRequest/identityRequest.jsf#/requests';

      if (data.status === 'Provisioned') {
        return <Tag color={token['bright-success']}>Provisioned</Tag>;
      }

      return (
        <>
          <div>
            <Popover
              content={
                data.pendingTraining ? (
                  <div>
                    <ul style={{ paddingLeft: 0, listStyleType: 'none' }}>
                      {data.pendingTraining.map((x) => (
                        <li>{x.name}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <ApproverTable
                    accessSource={access.accessSource}
                    externalId={access.externalId}
                    name={access.name}
                    style={{ marginLeft: -40 }}
                  />
                )
              }
              title={data.pendingTraining ? 'Pending Training' : 'Approver Workgroup'}
            >
              <Tag style={{ color: token['carbon-shade-2'] }} color={token['bright-warning']}>
                {data.status}
              </Tag>
            </Popover>
          </div>
          <div>
            {(access.accessSource === AccessSource.AGS_ENTITLEMENT ||
              access.accessSource === AccessSource.AGS_ROLE) && (
              <a href={href} target="_blank" rel="noreferrer">
                <LinkOutlined />
                {data.pendingTraining ? ' SABA' : ' AGS'}
              </a>
            )}
          </div>
        </>
      );
    }

    return (
      (access.accessSource === AccessSource.AGS_ENTITLEMENT || access.accessSource === AccessSource.AGS_ROLE) && (
        <Popover content="SAAF does not have the ability to check status once an AGS request is completed. Please go to AGS to check the result.">
          <a
            href={'https://ags.intel.com/identityiq/identityRequest/identityRequest.jsf#/requests'}
            target="_blank"
            rel="noreferrer"
          >
            <LinkOutlined /> AGS
          </a>
        </Popover>
      )
    );
  }

  // if access and user are not provided, return one of the old statuses
  // this will mainly be used for archive to see status transitions
  // and will probably go away after SAAF remodel
  switch (status) {
    case SubscriptionAccessStatus.REJECTED:
    case SubscriptionAccessStatus.EXPIRED:
    case SubscriptionAccessStatus.CANCELLED_AT_SOURCE:
      return <Tag color={token['bright-error']}>{getStatusText(status)}</Tag>;
    case SubscriptionAccessStatus.APPROVED:
    case SubscriptionAccessStatus.COMPLETED:
      return <Tag color={token['bright-success']}>{getStatusText(status)}</Tag>;
    default:
      return <Tag color="#8c8c8c">{getStatusText(status)}</Tag>;
  }
};

export const SubscriptionAccessStatusRendererOld = ({ access, status }: SubscriptionAccessStatusRendererProps) => {
  const { token } = Theme.useToken();

  switch (status) {
    case SubscriptionAccessStatus.NEW:
    case SubscriptionAccessStatus.QUEUED:
      return <Tag color="#8c8c8c">{getStatusText(status)}</Tag>;
    case SubscriptionAccessStatus.RETRY:
    case SubscriptionAccessStatus.PENDING:
    case SubscriptionAccessStatus.WAITING_ON_DEPENDENCY:
      return (
        <Flex>
          <Tag style={{ color: token['carbon-shade-2'] }} color={token['bright-warning']}>
            {getStatusText(status)}
          </Tag>
          {(access?.accessSource === AccessSource.AGS_ENTITLEMENT ||
            access?.accessSource === AccessSource.AGS_ROLE) && (
            <a
              href={'https://ags.intel.com/identityiq/identityRequest/identityRequest.jsf#/requests'}
              target="_blank"
              rel="noreferrer"
            >
              <MonitorOutlined />
            </a>
          )}
        </Flex>
      );
    case SubscriptionAccessStatus.ERROR:
    case SubscriptionAccessStatus.REJECTED:
    case SubscriptionAccessStatus.EXPIRED:
    case SubscriptionAccessStatus.CANCELLED_AT_SOURCE:
      return <Tag color={token['bright-error']}>{getStatusText(status)}</Tag>;
    case SubscriptionAccessStatus.CANCELLED:
    case SubscriptionAccessStatus.APPROVED:
    case SubscriptionAccessStatus.COMPLETED:
      return <Tag color={token['bright-success']}>{getStatusText(status)}</Tag>;
    default:
      return <Tag color="#8c8c8c">{getStatusText(status)}</Tag>;
  }
};

export const SubscriptionAccessStatusRenderer = (props: SubscriptionAccessStatusRendererProps) => {
  const { isFeatureEnabled } = FeatureFlags.useFeatureFlags();

  return isFeatureEnabled('live-access-request-status') ? (
    <SubscriptionAccessStatusRendererNew {...props} />
  ) : (
    <SubscriptionAccessStatusRendererOld {...props} />
  );
};

const getStatusText = (subscriptionAccessStatus: SubscriptionAccessStatus) => {
  return startCase(subscriptionAccessStatus.toLowerCase());
};
